import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { partTypeTire } from "../../../../constants/partTypes";
import { useLanguage } from "../../../../i18n/useLanguage";
import { useRemoveItemFromCart } from "../../../../queries/useRemoveItemFromCart";
import { useUpdateItemInCart } from "../../../../queries/useUpdateItemInCart";
import { useDealerPricingNotes } from "../../../../stores/hooks/useDealerPricingNotes";
import { OrderItemTire } from "../../../../stores/slices/cartSlice";
import { Button } from "../../../../ui/Button/Button";
import { Image } from "../../../../ui/Image/Image";
import { KeyValue } from "../../../../ui/KeyValue/KeyValue";
import { TrashIcon } from "../../../../ui/icons/TrashIcon";
import { fixTireImageUrl } from "../../../../util/fixTireImageUrl";
import { getPartFallbackSrc } from "../../../../util/getPartFallbackSrc";
import { getTiresizeAndClass } from "../../../../util/getTireClassAndSize";
import { PriceSelect } from "../../../PriceSelect/PriceSelect";
import { TireLinkLogo } from "../../../TireLinkLogo/TireLinkLogo";
import { getTirePartNumber } from "../../../Tires/Common/getTirePartNumber";
import { useTireLinkLogoIsVisible } from "../../useTireLinkLogoIsVisible";
import { LineItems } from "./LineItems/LineItems";

type OrderItemProps = {
  isOrder: boolean;
  orderItem: OrderItemTire;
};

export const CartItemTire: FunctionComponent<OrderItemProps> = ({
  isOrder,
  orderItem,
}) => {
  const updateItemInCart = useUpdateItemInCart();
  const { t } = useTranslation();
  const [language] = useLanguage();
  const removeItemFromCart = useRemoveItemFromCart();
  const shouldRenderTireLinkLogo = useTireLinkLogoIsVisible(orderItem);
  const packageNotes = useDealerPricingNotes(orderItem.itemType);

  return (
    <div
      className="border-b md:border md:border-primary pb-6 md:p-4 md:rounded-panel space-y-4"
      key={orderItem.itemId}
    >
      <div className="flex items-start space-x-4">
        <div>
          <Image
            className={clsx(
              "w-14 h-14 md:w-32 md:h-32 bg-input rounded-section object-cover object-center",
              "text-transparent scale-x-[-1]"
            )}
            src={fixTireImageUrl(
              orderItem.itemPart.partDetails.primaryPart.tireImageLink
            )}
            alt="Product Image"
            errorClassName="opacity-20 blur-[8px]"
            fallbackSrc={getPartFallbackSrc(partTypeTire)}
          />
          <img
            src={
              orderItem.itemPart.partDetails.primaryPart.tireManufacturerImage
            }
            alt="Manufacturer Logo"
            className="w-14 object-cover object-center text-transparent mt-1 md:hidden"
          />
        </div>
        <div className="flex-1">
          <div className="flex space-x-2 items-start">
            <h2 className="flex-1 text-lg md:text-2xl md:mb-2 font-medium">
              {orderItem.itemTitle[language] || orderItem.itemTitle["en"]}
            </h2>
            {!isOrder && (
              <Button
                onClick={(): void => {
                  removeItemFromCart.send(orderItem);
                }}
                aria-label="removeItemFromCart"
                busy={removeItemFromCart.loading}
                className="text-ink-2 font-light -mt-0.5 px-2"
                disabled={removeItemFromCart.loading}
              >
                <TrashIcon />
                <span className="hidden md:inline">{t("cart.remove")}</span>
              </Button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3">
            <div className="hidden sm:block space-y-1">
              <KeyValue>
                <>{t("searchResults.partNumber")}:</>
                <>{getTirePartNumber(orderItem.itemPart)}</>
              </KeyValue>
              <KeyValue>
                <>{t("searchResults.tireSize")}:</>
                <>{getTiresizeAndClass(orderItem.itemPart)}</>
              </KeyValue>
              <KeyValue>
                <>{t("searchResults.loadSpeedPly")}:</>
                <>
                  {orderItem.itemPart.partDetails.primaryPart.tireLoadRating}/
                  {orderItem.itemPart.partDetails.primaryPart.tireSpeedIndex}/
                  {orderItem.itemPart.partDetails.primaryPart.tirePlyRating}
                </>
              </KeyValue>
              {shouldRenderTireLinkLogo && (
                <TireLinkLogo className="h-4 mt-1" />
              )}
            </div>
            <div className="space-y-1">
              <PriceSelect
                onChange={([{ pricingOption }]): void => {
                  updateItemInCart.send(orderItem, pricingOption);
                }}
                busy={updateItemInCart.loading}
                disabled={removeItemFromCart.loading || isOrder}
                isStaggered={false}
                itemTireSizeClassName="hidden"
                orderItem={orderItem}
                part={orderItem.itemPart}
              />
              {packageNotes && <div className="text-sm">{packageNotes}</div>}
            </div>
            <div className="hidden xl:flex items-end justify-end">
              <img
                src={
                  orderItem.itemPart.partDetails.primaryPart
                    .tireManufacturerImage
                }
                alt="Manufacturer Logo"
                className="h-8 object-cover object-center text-transparent mt-1 "
              />
            </div>
          </div>
        </div>
      </div>
      <LineItems isOrder={isOrder} orderItem={orderItem} />
    </div>
  );
};
